import { createEnrichedLogger } from '@/utils/logger/create-enriched-logger';
import { type CustomLogger } from '@/utils/logger/types';

/**
 * Reader for the per-session correlation IDs. Returns `null` for both
 * fields when no session is active.
 *
 * SCR-4118: this logger is imported by every dictation slice, which is in
 * turn imported by `useDictationStore`. Importing the store directly here
 * would form a cycle (dictationLogger → useDictationStore → slices →
 * dictationLogger) that breaks Zustand's `create(factory)` at module-init
 * time, because the factory runs eagerly and would see partially-evaluated
 * slice exports. Instead `useDictationStore.ts` registers a reader via
 * `setDictationIdReader` once the store is constructed.
 */
type DictationIdReader = () => {
  dictationId: string | null;
  dictationRecordingId: string | null;
};

let readDictationIdsState: DictationIdReader = () => ({
  dictationId: null,
  dictationRecordingId: null,
});

export const setDictationIdReader = (reader: DictationIdReader): void => {
  readDictationIdsState = reader;
};

/**
 * Build the snake_case correlation pair to merge into log payloads. Empty
 * object when no session is active so absence of the fields means "no
 * dictation in flight" (e.g. config-sync errors before any dictation
 * started). Keys are snake_case to match the multipart field names the
 * chunk POST sends to `/transcribe-chunk-dictation`, so support and log
 * pipelines join on the same vocabulary as the backend.
 */
const readDictationIds = (): Record<string, string> => {
  const ids = readDictationIdsState();
  if (!ids.dictationId || !ids.dictationRecordingId) return {};
  return {
    dictation_id: ids.dictationId,
    dictation_recording_id: ids.dictationRecordingId,
  };
};

/**
 * Merge IDs onto the top level of the payload (alongside `event_type` /
 * `details`) rather than into `details`. Existing call sites pass `details`
 * values that are sometimes plain objects (`{ error_reason: '...' }`) and
 * sometimes Error / AxiosError instances — spreading IDs into an Error
 * would drop the prototype and break Datadog's stack-trace extraction.
 * Top-level keys are also what Pino's `bindings` already use for the same
 * purpose (`module: 'dictation'`), so this stays consistent with the
 * surrounding logger shape.
 */
const enrich = (payload: unknown): unknown => {
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'event_type' in (payload as Record<string, unknown>)
  ) {
    const ids = readDictationIds();
    if (Object.keys(ids).length === 0) return payload;
    return { ...ids, ...(payload as Record<string, unknown>) };
  }
  return payload;
};

/**
 * Module-scoped logger for dictation. Auto-merges `dictation_id` /
 * `dictation_recording_id` into the payload on every call so individual
 * call sites stay free of correlation boilerplate. Child loggers inherit
 * the enrichment (shared decorator: `createEnrichedLogger`).
 */
export const dictationLogger: CustomLogger = createEnrichedLogger('dictation', enrich);
